import { getLRDisclaimer } from "@/utils/lrDisclaimer";

/** Build printable LR sheets with one full-size landscape A4 page per copy. */
function printLRDocument(snapshots, targetWindow = null, copyMode = "both") {
  if (!snapshots?.length) return;

  const win = targetWindow || window.open("", "_blank", "width=900,height=700,toolbar=no,menubar=no");
  if (!win) { alert("Pop-up blocked. Please allow pop-ups for this site and try again."); return; }
  win.document.open();

  const logoUrl = `${window.location.origin}/city_connect_logo.png`;
  const logoHtml = `<img src="${logoUrl}" alt="City Connect" class="brand-logo" />`;

  const renderCopy = (t, copyLabel) => {
  // ── Resolve field values (new enriched fields first, legacy fallback) ──
  const tDate = t.transfer_date
    ? new Date(t.transfer_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "—";

  const consignorName    = t.consignor_name    || t.customer_name  || "—";
  const consignorPhone   = t.consignor_phone   || t.customer_phone || "—";
  const alternateNumber  = t.alternate_number || "";
  const pickupAddress    = t.pickup_address    || t.origin_label   || "—";
  const consigneeName    = t.consignee_name    || t.customer_name  || "—";
  const consigneePhone   = t.consignee_phone   || t.customer_phone || "—";
  const dropAddress      = t.drop_address      || t.destination_label || "—";
  const rawToPayAmount   = t.to_pay_amount || t.amount_at_delivery || "";
  const hasToPay         = Number(rawToPayAmount) > 0;
  const toPayStatus      = hasToPay ? "YES" : "NO";
  const toPayAmt         = hasToPay ? rawToPayAmount : "—";
  const insuranceOpted   = t.insurance_opted   || (t.transit_insurance ? "YES" : "NO");
  const riskCoverage     = t.risk_coverage_type
    || (t.carrier_risk ? "Carrier's Risk" : "Owner's Risk");
  const ewayBill         = t.eway_bill_no || "NA";
  const bookingIncharge  = t.booking_incharge || t.assigned_to_name || "—";
  const vehicleNo        = t.vehicle_display  || "—";
  const vehicleType      = t.vehicle_type
    ? `${t.vehicle_type}${t.closed_vehicle ? " - Closed Vehicle" : ""}`
    : "—";

  const quantityHtml = (t.load_details || []).length > 0
    ? (t.load_details || []).map(ld =>
        `<li>${ld.load_name || "Load"}${ld.handle_with_care ? " <span style='color:#B45309;font-weight:900'>⚠ HWC</span>" : ""}</li>`
      ).join("")
    : "<li>—</li>";
  const disclaimer = getLRDisclaimer(t.load_details || []);

  const totalWeight = (t.load_details || []).reduce((sum, ld) => ld.weight ? sum + parseFloat(ld.weight) : sum, 0);
  const weightStr   = totalWeight > 0 ? `${totalWeight} kg` : "NA";

  return `
    <div class="lr-block">
      <table class="hdr-table">
        <tr>
          <td class="hdr-logo">${logoHtml}</td>
          <td class="hdr-info">
            <div class="company-addr">Near Lakshmipuram Palace, Puzhavathu, Lakshmipuram, Changanacherry, Kottayam, Kerala - 686101</div>
            <div class="company-addr"><strong>GSTIN: 32BFKPR7082B1ZG</strong></div>
            <div class="company-addr">Email: cityconnectnetwork@gmail.com &nbsp;|&nbsp; Website: www.cityconnectlogistics.com</div>
            <div class="company-addr">Phone: 80506 84433 &nbsp;/&nbsp; 70229 09521</div>
          </td>
        </tr>
      </table>

      <div class="lr-title">CONSIGNMENT NOTE &nbsp;-&nbsp; ${copyLabel}</div>

      <table class="data-table">
        <tr>
          <td class="half-l"><span class="k">L.R. NO :</span> <strong>${t.lr_number || "—"}</strong></td>
          <td class="half-r"><span class="k">Booking Date :</span> ${tDate}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l"><span class="k">Vehicle No :</span> ${vehicleNo}</td>
          <td class="half-r"><span class="k">Vehicle Type :</span> ${vehicleType}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="khead">Consignor Name :</td><td class="vhalf">${consignorName}</td>
          <td class="khead">Consignee Name :</td><td class="vhalf">${consigneeName}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l"><span class="k">Phone :</span> ${consignorPhone}${alternateNumber ? ` &nbsp;|&nbsp; <span class="k">Alternate :</span> ${alternateNumber}` : ""}</td>
          <td class="half-r"><span class="k">Phone :</span> ${consigneePhone}${alternateNumber ? ` &nbsp;|&nbsp; <span class="k">Alternate :</span> ${alternateNumber}` : ""}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l addr"><span class="k">Pick-Up Address :</span><br>${pickupAddress}</td>
          <td class="half-r addr"><span class="k">Drop Address :</span><br>${dropAddress}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l desc"><span class="k">Description (Said to Contain) :</span><p class="goods-disclaimer">${disclaimer}</p></td>
          <td class="half-r"><span class="k">Value of Consignment :</span> ${t.value_of_consignment || "NA"}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td rowspan="2" class="freight-cell">
            <div class="k" style="margin-bottom:4px">Payment :</div>
            <table class="inner-table">
              <tr>
                <td class="k">TO PAY</td>
                <td class="k">TO PAY AMOUNT</td>
              </tr>
              <tr>
                <td>${toPayStatus}</td>
                <td><strong>${toPayAmt}</strong></td>
              </tr>
            </table>
          </td>
          <td class="qew-head k quantity-col">QUANTITY :</td>
          <td class="qew-head k eway-col">E-WAY BILL NO :</td>
          <td class="qew-head k weight-col">WEIGHT :</td>
        </tr>
        <tr>
          <td class="qew-val load-quantity quantity-col"><ul>${quantityHtml}</ul></td>
          <td class="qew-val eway-col">${ewayBill}</td>
          <td class="qew-val weight-col">${weightStr}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l">
            <span class="k">Insurance Opted :</span> ${insuranceOpted} &nbsp;&nbsp;
            <span class="k">Risk Coverage :</span> ${riskCoverage}
          </td>
          <td class="half-r"><span class="k">Booking Incharge :</span> ${bookingIncharge}</td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l remarks"><span class="k">REMARKS AT TIME OF RECEIVING :</span><br><br><br></td>
          <td class="half-r remarks">
            <div class="k" style="text-decoration:underline">DETAILS AT TIME OF RECEIVING — POD</div>
            <br>Date :&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <br><br>Time :
          </td>
        </tr>
      </table>

      <table class="data-table">
        <tr>
          <td class="half-l"><span class="k">BOOKING BY : </span> ${bookingIncharge}</td>
          <td class="half-r sig"><span class="k">RECEIVER'S SIGNATURE &amp; SEAL</span></td>
        </tr>
      </table>

      <div class="electronic-declaration">This is an electronically generated statement and does not need a signature</div>
    </div>`;
  };

  const customerCopies = snapshots.map(snapshot => renderCopy(snapshot, "Customer's Copy"));
  const driverCopies = snapshots.map(snapshot => renderCopy(snapshot, "Driver's Copy"));
  const makeSheets = blocks => blocks.map(block => `<div class="lr-sheet">${block}</div>`).join("");
  const sheetsHtml = [
    copyMode !== "driver" ? makeSheets(customerCopies) : "",
    copyMode !== "customer" ? makeSheets(driverCopies) : "",
  ].join("");
  const titleSnapshot = snapshots[0];

  win.document.write(`<!DOCTYPE html>
<html><head><title>LR — ${snapshots.length === 1 ? (titleSnapshot.lr_number || "Consignment") : `${snapshots.length} Consignments`}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #0F172A; }
  .lr-sheet { width: 283mm; height: 196mm; page-break-after: always; break-after: page; }
  .lr-sheet:last-child { page-break-after: auto; break-after: auto; }
  .lr-block { width: 100%; height: 100%; padding: 4mm 5mm; border: 1.5px solid #1B2A6B; overflow: hidden; }
  .hdr-table { width: 100%; border-collapse: collapse; margin-bottom: 0; border-bottom: 1px solid #1B2A6B; }
  .hdr-logo { width: 180px; padding: 5px 8px; vertical-align: middle; border-right: 1px solid #1B2A6B; }
  .brand-logo { display: block; width: 158px; height: auto; object-fit: contain; }
  .hdr-info { padding: 5px 10px; vertical-align: middle; text-align: left; }
  .company-addr { font-size: 10.5px; color: #334155; line-height: 1.35; }
  .lr-title { background: #1B2A6B; color: #FFFFFF; text-align: center; font-size: 15px; font-weight: 900; letter-spacing: 1.5px; padding: 5px; border-bottom: 1px solid #1B2A6B; }
  .data-table { width: 100%; border-collapse: collapse; border-bottom: 1px solid #CBD5E1; }
  .data-table td { padding: 4px 7px; border-right: 1px solid #CBD5E1; font-size: 11.5px; line-height: 1.3; vertical-align: top; }
  .data-table td:last-child { border-right: none; }
  .half-l { width: 50%; }
  .half-r { width: 50%; }
  .khead { font-weight: 700; background: #F8FAFC; width: 15%; white-space: nowrap; }
  .vhalf { width: 35%; }
  .k { font-weight: 700; color: #1B2A6B; }
  .addr { height: 48px; vertical-align: top; }
  .desc ul { padding-left: 10px; margin: 1px 0; }
  .desc li { font-size: 11px; }
  .goods-disclaimer { margin-top: 3px; color: #1B2A6B; font-size: 11px; font-weight: 800; }
  .remarks { height: 72px; vertical-align: top; }
  .sig { text-align: right; vertical-align: bottom; padding-bottom: 4px; }
  .freight-cell { width: 50%; border-right: 1px solid #CBD5E1; padding: 4px 7px; }
  .inner-table { width: 100%; border-collapse: collapse; margin-top: 1px; }
  .inner-table td { border: 1px solid #E2E8F0; padding: 3px 5px; font-size: 11px; }
  .qew-head { width: 17%; text-align: center; font-size: 10.5px; border-bottom: 1px solid #CBD5E1; padding: 4px 5px; }
  .qew-val  { width: 17%; text-align: center; font-size: 11px; padding: 4px 5px; }
  .quantity-col { width: 28%; }
  .eway-col { width: 12%; }
  .weight-col { width: 10%; }
  .load-quantity { text-align: left; }
  .load-quantity ul { margin: 0; padding-left: 10px; }
  .electronic-declaration { padding-top: 5px; text-align: center; color: #475569; font-size: 10px; font-style: italic; }
  @media print {
    @page { size: A4 landscape; margin: 7mm; }
    .lr-block { border: 1.5px solid #1B2A6B; }
  }
</style></head>
<body>${sheetsHtml}</body>
<script>window.onload = function(){ window.print(); }<\/script>
</html>`);
  win.document.close();
}

/** Print one consignment as two separate A4 pages: customer first, then driver. */
export function printSingleLR(snapshot, targetWindow = null) {
  return printLRDocument(snapshot ? [snapshot] : [], targetWindow, "both");
}

/** Print multiple consignments with customer copies first, then driver copies, one landscape page per copy. */
export function printMultipleLR(snapshots, targetWindow = null, copyMode = "both") {
  return printLRDocument((snapshots || []).filter(Boolean), targetWindow, copyMode);
}
